import {
  Button,
  createFileCache,
  DEFAULT_MAX_AGE_MS,
  DEFAULT_MAX_SIZE_BYTES,
  type CacheStats
} from "@9amhealth/shared";
import type { FC } from "react";
import React from "react";
import { StorageController } from "src/state/StorageBloc/StorageBloc";

const fileCache = createFileCache();

const formatBytes = (bytes: number): string => {
  if (bytes === 0) {
    return "0 B";
  }
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

const formatDuration = (ms: number): string => {
  const days = Math.floor(ms / (24 * 60 * 60 * 1000));
  if (days > 0) {
    return `${days} day${days > 1 ? "s" : ""}`;
  }
  const hours = Math.floor(ms / (60 * 60 * 1000));
  if (hours > 0) {
    return `${hours} hour${hours > 1 ? "s" : ""}`;
  }
  const minutes = Math.floor(ms / (60 * 1000));
  return `${minutes} minute${minutes > 1 ? "s" : ""}`;
};

const DebugFileCache: FC = () => {
  const [stats, setStats] = React.useState<CacheStats | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [lastAction, setLastAction] = React.useState<string | null>(null);

  const userId = StorageController.activeUserId;

  const loadStats = React.useCallback(async () => {
    if (!userId) {
      setError("No active user");
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const cacheStats = await fileCache.getCacheStats(userId);
      setStats(cacheStats);
      setLastAction(`Loaded stats at ${new Date().toLocaleTimeString()}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load stats");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  React.useEffect(() => {
    void loadStats();
  }, [loadStats]);

  const clearUserCache = async () => {
    if (!userId) {
      return;
    }

    setLoading(true);
    try {
      await fileCache.clearUserFileCache(userId);
      setLastAction(
        `Cleared cache for user at ${new Date().toLocaleTimeString()}`
      );
      await loadStats();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to clear cache");
    } finally {
      setLoading(false);
    }
  };

  const clearAllCache = async () => {
    setLoading(true);
    try {
      await fileCache.clearFileCache();
      setLastAction(`Cleared all cache at ${new Date().toLocaleTimeString()}`);
      await loadStats();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to clear all cache");
    } finally {
      setLoading(false);
    }
  };

  const testCacheFile = async () => {
    if (!userId) {
      return;
    }

    setLoading(true);
    try {
      const testBlob = new Blob([`Test file content ${Date.now()}`], {
        type: "text/plain"
      });
      const testId = `debug-test-${Date.now()}`;
      await fileCache.cacheFile(
        testId,
        userId,
        testBlob,
        "test.txt",
        "text/plain"
      );
      setLastAction(
        `Cached test file "${testId}" at ${new Date().toLocaleTimeString()}`
      );
      await loadStats();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to cache test file");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div style={{ marginBottom: "10px" }}>
        <Button
          size="medium"
          onPress={() => void loadStats()}
          isDisabled={loading}
        >
          Refresh Stats
        </Button>
        <Button
          size="medium"
          onPress={() => void testCacheFile()}
          isDisabled={loading || !userId}
        >
          Add Test File
        </Button>
        <Button
          size="medium"
          onPress={() => void clearUserCache()}
          isDisabled={loading || !userId}
        >
          Clear User Cache
        </Button>
        <Button
          size="medium"
          onPress={() => void clearAllCache()}
          isDisabled={loading}
        >
          Clear All Cache
        </Button>
      </div>

      {error && (
        <div
          style={{
            padding: "8px",
            backgroundColor: "#ffebee",
            borderRadius: "4px",
            color: "#c62828",
            marginBottom: "10px"
          }}
        >
          Error: {error}
        </div>
      )}

      <div style={{ marginTop: "10px" }}>
        <p>
          <strong>Current User:</strong> <code>{userId ?? "none"}</code>
        </p>

        {stats && (
          <div
            style={{
              marginTop: "10px",
              padding: "8px",
              backgroundColor: "#f0f0f0",
              borderRadius: "4px"
            }}
          >
            <p>
              <strong>Cache Stats:</strong>
            </p>
            <ul style={{ margin: "5px 0", paddingLeft: "20px" }}>
              <li>
                Files cached: <strong>{stats.count}</strong>
              </li>
              <li>
                Total size: <strong>{formatBytes(stats.totalSize)}</strong>
              </li>
            </ul>
          </div>
        )}

        {lastAction && (
          <p style={{ marginTop: "10px", fontSize: "9px", color: "#666" }}>
            Last action: {lastAction}
          </p>
        )}
      </div>

      <div
        style={{
          marginTop: "10px",
          padding: "8px",
          backgroundColor: "#e3f2fd",
          borderRadius: "4px",
          fontSize: "9px"
        }}
      >
        <p>
          <strong>Config:</strong>
        </p>
        <ul style={{ margin: "5px 0", paddingLeft: "20px" }}>
          <li>Max age: {formatDuration(DEFAULT_MAX_AGE_MS)}</li>
          <li>Max size: {formatBytes(DEFAULT_MAX_SIZE_BYTES)} per user</li>
          <li>Encryption: AES-GCM 256-bit</li>
          <li>Storage: IndexedDB (encrypted)</li>
          <li>Keys: localStorage (per user)</li>
        </ul>
      </div>
    </div>
  );
};

export default DebugFileCache;
